'use client';

import React, { useEffect, useRef } from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { ClipboardIcon, CalendarIcon, DocumentIcon, ChecklistIcon, InfoIcon, DollarSignIcon, RecipeScalerIcon } from '../Icons';
import { recipeDatabase } from '../../data/recipeDatabase';
import { Chart, BarController, CategoryScale, LinearScale, Title, Tooltip, Legend, BarElement } from 'chart.js';

interface ManagementDashboardProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

const StatCard: React.FC<{ title: string, value: string, icon: React.ReactNode }> = ({ title, value, icon }) => (
    <div className="bg-white p-6 rounded-xl border border-brand-border">
        <div className="flex items-center gap-4">
            <div className="bg-brand-pink-light text-brand-pink p-3 rounded-full">
                {icon}
            </div>
            <div>
                <p className="text-sm font-semibold text-brand-text-body">{title}</p>
                <p className="text-2xl font-bold text-brand-text-title">{value}</p>
            </div>
        </div>
    </div>
);

const ToolLinkCard: React.FC<{ title: string, description: string, icon: React.ReactNode, onClick: () => void }> = ({ title, description, icon, onClick }) => (
    <button onClick={onClick} className="bg-white p-6 rounded-xl border border-brand-border text-left w-full hover:shadow-lg hover:border-brand-pink/30 transition-all duration-300">
        <div className="flex items-start gap-4">
            <div className="bg-brand-pink-light text-brand-pink p-3 rounded-full flex-shrink-0 mt-1">
                {icon}
            </div>
            <div>
                <h3 className="text-lg font-bold text-brand-text-title">{title}</h3>
                <p className="text-sm text-brand-text-body/80 mt-1">{description}</p>
            </div>
        </div>
    </button>
);

const RecipeCategoryChart: React.FC = () => {
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstanceRef = useRef<Chart | null>(null);

    useEffect(() => {
        Chart.register(BarController, CategoryScale, LinearScale, Title, Tooltip, Legend, BarElement);

        if (chartRef.current) {
            const categories = Object.keys(recipeDatabase);
            const recipeCounts = categories.map(cat => recipeDatabase[cat].length);

            const data = {
                labels: categories,
                datasets: [{
                    label: 'Number of Recipes',
                    data: recipeCounts,
                    backgroundColor: 'rgba(230, 90, 136, 0.6)',
                    borderColor: 'rgba(230, 90, 136, 1)',
                    borderWidth: 1,
                    borderRadius: 4,
                }]
            };

            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }

            chartInstanceRef.current = new Chart(chartRef.current, {
                type: 'bar',
                data: data,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        title: { display: true, text: 'Recipe Count per Category', font: { size: 16, family: "'Lato', sans-serif" } }
                    },
                    scales: {
                        y: { beginAtZero: true, grid: { color: '#EDE7E4' } },
                        x: { grid: { display: false } }
                    }
                }
            });
        }

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, []);

    return <div className="bg-white p-4 rounded-xl border border-brand-border h-80"><canvas ref={chartRef}></canvas></div>;
};

export const ManagementDashboard: React.FC<ManagementDashboardProps> = ({ setActiveTool, breadcrumbs }) => {
    const totalRecipes = Object.values(recipeDatabase).reduce((sum, recipes) => sum + recipes.length, 0);

    return (
        <ToolContainer
            title="Free Home Bakery Management Dashboard"
            description="Your free home bakery dashboard to manage orders, schedules, and recipes. The perfect starting point for your cottage food business."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="management-dashboard"
        >
            <div className="space-y-8">
                <div>
                    <h2 className="font-serif text-2xl font-bold text-brand-text-title mb-4">Quick Stats</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <StatCard title="Active Orders" value="3" icon={<ClipboardIcon className="w-6 h-6" />} />
                        <StatCard title="Tasks for Today" value="5" icon={<CalendarIcon className="w-6 h-6" />} />
                        <StatCard title="Total Recipes" value={totalRecipes.toString()} icon={<ChecklistIcon className="w-6 h-6" />} />
                    </div>
                </div>

                <div>
                    <h2 className="font-serif text-2xl font-bold text-brand-text-title mb-4">Content Overview</h2>
                    <RecipeCategoryChart />
                </div>

                <div>
                    <h2 className="font-serif text-2xl font-bold text-brand-text-title mb-4">Professional Bakery Management Software Tools</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <ToolLinkCard
                            title="Cake Pricing"
                            description="Price your cakes accurately by calculating COGS and labor."
                            icon={<DollarSignIcon className="w-6 h-6" />}
                            onClick={() => setActiveTool('cake-pricing')}
                        />
                        <ToolLinkCard
                            title="Recipe Scaler"
                            description="Scale your yields perfectly while maintaining consistency."
                            icon={<RecipeScalerIcon className="w-6 h-6" />}
                            onClick={() => setActiveTool('recipe-scaler')}
                        />
                        <ToolLinkCard
                            title="Order Management"
                            description="Track customer orders from placement to pickup."
                            icon={<ClipboardIcon className="w-6 h-6" />}
                            onClick={() => setActiveTool('order-management')}
                        />
                        <ToolLinkCard
                            title="Bakery Order Planner"
                            description="Organize your baking tasks and create efficient schedules."
                            icon={<CalendarIcon className="w-6 h-6" />}
                            onClick={() => setActiveTool('bakery-order-planner')}
                        />
                        <ToolLinkCard
                            title="SaaS Upgrade (Bakery Pro)"
                            description="Get advanced invoicing, client CRM, and cloud sync."
                            icon={<DocumentIcon className="w-6 h-6" />}
                            onClick={() => setActiveTool('saas')}
                        />
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t border-brand-border">
                    <h3 className="font-serif font-bold text-2xl mb-4 text-brand-text-title">Tips for Effective Bakery Management</h3>
                    <ul className="list-disc pl-5 text-brand-text-body/90 leading-relaxed space-y-2">
                        <li><strong>Consistency is Key:</strong> Use tools like the Baker's Percentage and Recipe Scaler to ensure your products are consistent every single time.</li>
                        <li><strong>Know Your Numbers:</strong> Regularly use the Cake Pricing Calculator to ensure you're profitable. Keep track of rising ingredient costs.</li>
                        <li><strong>Master Your Schedule:</strong> A well-planned schedule prevents chaos. Use the Bakery Order Planner to work backwards from deadlines.</li>
                        <li><strong>Customer Service Matters:</strong> The Order Management tool helps you stay organized, preventing missed orders or mixed-up details.</li>
                    </ul>
                </div>
            </div>
            <ManagementDashboardContent />
        </ToolContainer>
    );
};

const ManagementDashboardContent: React.FC = () => (
    <div className="mt-12 grid md:grid-cols-2 gap-8 pt-8 border-t border-brand-border">
        <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
            <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                <InfoIcon className="w-6 h-6 text-brand-pink" />
                How to Use Your Bakery Dashboard
            </h3>
            <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                <li><strong>Track Active Orders:</strong> See a quick snapshot of how many orders are currently in progress.</li>
                <li><strong>Monitor Daily Tasks:</strong> Check your tasks to stay on top of your production schedule.</li>
                <li><strong>Manage Recipes:</strong> View your total recipe count across categories.</li>
                <li><strong>Access Tools:</strong> Use the quick links to jump to essential tools like "Cake Pricing" or "Order Management".</li>
            </ol>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
            <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Frequently Asked Questions</h3>
            <div className="space-y-4">
                <details className="group">
                    <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                        Is my data saved?
                        <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                        Currently, this dashboard provides a session-based overview. For persistent data storage and CRM features, upgrade to <strong>Bakery Pro</strong>.
                    </p>
                </details>
                <details className="group">
                    <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                        Is this free for commercial use?
                        <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                        Yes! This dashboard is designed to be a free resource for home bakers and small bakery owners to manage their business effectively.
                    </p>
                </details>
            </div>
        </div>
    </div>
);
